import {
    createFundWatchData,
    getSubscriptionEntityId,
    getSubscriptionSetCode,
} from "@hsl/fc-subscription/lib";
import { type SelectDocumentsStep } from "@hsl/fc-subscription/types";
import type {
    FundDataWithType,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import {
    type SubscriptionsCreate,
    type UserDetails,
} from "../../../../../openapi";
import { type SelectionDocument, type UpdateArgs } from "./types";

export function getDataWithTypeId(
    data: FundDataWithType | ShareClassDataWithType,
) {
    return `${data.data.id}`;
}

export function createSubscriptionsHelper(
    doc: SelectionDocument,
    restrict?: { funds?: string[]; shareClasses?: string[] },
    type: "WATCHLIST" | "FULL" = "FULL",
    funds?: FundDataWithType[],
) {
    const { scSelector, subscription_data: subsData } = doc;
    const getFundType = (fundId: string) => {
        if (funds && funds.length) {
            const fund = funds.find((f) => `${f.data.id}` === fundId);
            return fund?.type ?? "";
        }
        return "";
    };
    const restrictIdx = (
        (scSelector ? restrict?.shareClasses : restrict?.funds) ?? []
    ).map((restrictId) =>
        (scSelector ? subsData.shareClasses : subsData.funds).findIndex(
            (id) => id === restrictId,
        ),
    );
    const subsMap = subsData.subsMap;
    const rSubsMap =
        restrictIdx.length > 0
            ? subsMap.filter((_, i) => restrictIdx.includes(i))
            : subsMap;
    const subscriptions: SubscriptionsCreate = rSubsMap.map(
        ([fundId, __, subId]) => {
            const entityId = getSubscriptionEntityId(subId);
            const setCode = getSubscriptionSetCode(subId);
            return {
                frequency: "DAILY",
                entityId: `${entityId}`,
                fundId: `${getFundType(fundId)}-${fundId}`,
                literatureSetCode: `${setCode}`,
                subscriptionType: type,
                subscriptionLevel: doc.scSelector ? "SHARE_CLASS" : "FUND",
                subscriptionId: subId,
            };
        },
    );
    return subscriptions;
}

export function updateSubscriptionsHelper(
    list: SubscriptionsCreate,
    dayOfWeek: UserDetails["dayOfWeek"],
    fundsData: FundDataWithType[],
    args?: UpdateArgs,
): Pick<SelectDocumentsStep, "complete" | "data"> {
    if (!args) {
        return {
            data: {
                subscriptions: [],
                dayOfWeek: "MONDAY",
            },
            complete: false,
        };
    }
    const { doc, subs, remove, funds, shareClasses } = args;
    const notThisDocSubs = doc
        ? list.filter((l) =>
              doc.subscription_data.subsMap
                  .filter(([fundId, scId]) => {
                      const entityId = scId ?? fundId;
                      const entityIds = doc.scSelector ? shareClasses : funds;

                      return Array.isArray(entityIds) && entityIds.length
                          ? entityIds.some((id) => id === entityId)
                          : true;
                  })
                  .every(([_, __, subId]) => subId !== l.subscriptionId),
          )
        : subs
          ? list.filter((l) =>
                subs.every((sub) => sub.subscriptionId !== l.subscriptionId),
            )
          : [];
    const updatedData =
        subs && !remove ? notThisDocSubs.concat(subs) : notThisDocSubs;
    const complete = updatedData.length > 0;
    const subscribedFundsSet = updatedData.reduce((fundIds, sub) => {
        const [, fundId] = sub.fundId?.split("-") ?? [];
        if (fundId) {
            fundIds.add(fundId);
        }
        return fundIds;
    }, new Set<string>());
    const watchlist = Array.from(subscribedFundsSet)
        .map((fundId) => fundsData.find((fD) => `${fD.data.id}` === fundId))
        .filter((x) => Boolean(x))
        .map((fD) => createFundWatchData(fD!));
    return {
        data: {
            subscriptions: updatedData,
            watchlist,
            dayOfWeek: dayOfWeek ?? "MONDAY",
        },
        complete,
    };
}
